import React, { useState, useEffect } from 'react';
import { X, Edit, Receipt, CreditCard, User, Phone, MapPin, Clock, Play, CheckCircle, Truck, AlertCircle, Leaf, Plus, IndianRupee, Trash2, Calendar } from 'lucide-react';
import { Sale, Customer, Payment } from '../../types';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { orderService, customerService, paymentService, getOrderItems } from '../../services/firebase';
import ReceiptGenerator from '../receipt/ReceiptGenerator';
import { printReceiptWithLogo } from '../../utils/receiptUtils';
import { toDate, formatCurrency as formatCurrencyHelper, getOrderStatusColor } from '../../utils/helpers';

interface OrderDetailsModalProps {
  order: Sale | null;
  isOpen: boolean;
  onClose: () => void;
  onOrderUpdate?: (updatedOrder: Sale) => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  isOpen,
  onClose,
  onOrderUpdate
}) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptType, setReceiptType] = useState<'order' | 'payment' | 'completion'>('order');
  const [selectedOrder, setSelectedOrder] = useState<Sale | null>(null);

  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: 'Cash',
    notes: ''
  });

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Delivery date editing state
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [newDeliveryDate, setNewDeliveryDate] = useState('');

  const statusOptions = [
    { id: '1', name: 'Pending', description: 'Order received', order: 1 },
    { id: '2', name: 'In Progress', description: 'Order in production', order: 2 },
    { id: '3', name: 'Ready', description: 'Order ready for delivery', order: 3 },
    { id: '4', name: 'Delivered', description: 'Order delivered', order: 4 },
    { id: '5', name: 'Complete', description: 'Payment completed', order: 5 },
    { id: '6', name: 'Cancelled', description: 'Order cancelled', order: 6 },
    { id: '7', name: 'Deleted', description: 'Order deleted', order: 7 },
  ];

  useEffect(() => {
    if (order && isOpen) {
      setSelectedOrder(order);
      fetchOrderDetails();
    }
  }, [order, isOpen]);

  const fetchOrderDetails = async () => {
    if (!order) return;

    try {
      setLoading(true);
      const [customerData, paymentsData] = await Promise.all([
        customerService.getCustomerById(order.customerId),
        paymentService.getPaymentsByOrder(order.id)
      ]);

      setCustomer(customerData);
      setPayments(paymentsData);
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedOrder || !newStatus) return;
    
    const status = statusOptions.find(s => s.id === newStatus);
    if (!status) return;

    try {
      setLoading(true);
      await orderService.updateOrderStatus(selectedOrder.id, status);
      
      const updatedOrder = { ...selectedOrder, status };
      onOrderUpdate?.(updatedOrder);
      
      // Update the local order state to reflect the change immediately
      setSelectedOrder(updatedOrder);
      
      setShowStatusModal(false);
      setNewStatus('');
    } catch (error) {
      console.error('Error updating order status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder || !paymentForm.amount) return;

    const paymentAmount = parseFloat(paymentForm.amount);
    if (paymentAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (paymentAmount > selectedOrder.remainingAmount) {
      alert(`Payment amount (₹${paymentAmount}) cannot exceed remaining amount (₹${selectedOrder.remainingAmount})`);
      return;
    }

    try {
      setLoading(true);

      // Add payment record (this also updates order amounts in firebase.ts)
      await paymentService.addPayment({
        saleId: selectedOrder.id,
        customerId: selectedOrder.customerId,
        amount: paymentAmount,
        paymentMethod: paymentForm.paymentMethod,
        notes: paymentForm.notes
      });

      // Calculate new amounts for local state update
      const newAmountPaid = selectedOrder.amountPaid + paymentAmount;
      const newRemainingAmount = selectedOrder.totalAmount - newAmountPaid;

      // Update local state to reflect changes immediately
      const updatedOrder = {
        ...selectedOrder,
        amountPaid: newAmountPaid,
        remainingAmount: newRemainingAmount,
        // Update status to Complete if fully paid (matching firebase.ts logic)
        ...(newRemainingAmount <= 0 && {
          status: { id: '5', name: 'Complete', description: 'Payment completed', order: 5 }
        })
      };
      setSelectedOrder(updatedOrder);
      onOrderUpdate?.(updatedOrder);

      // Refresh payments list
      const paymentsData = await paymentService.getPaymentsByOrder(selectedOrder.id);
      setPayments(paymentsData);

      // Reset form and close modal
      setPaymentForm({ amount: '', paymentMethod: 'Cash', notes: '' });
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Error adding payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;

    try {
      setDeleting(true);
      const deletedStatus = { id: '7', name: 'Deleted', description: 'Order deleted', order: 7 };
      await orderService.updateOrderStatus(selectedOrder.id, deletedStatus);

      const updatedOrder = { ...selectedOrder, status: deletedStatus };
      onOrderUpdate?.(updatedOrder);

      setShowDeleteConfirm(false);
      onClose(); // Close the details modal after deletion
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Error deleting order. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateDeliveryDate = async () => {
    if (!selectedOrder) return;

    try {
      setLoading(true);
      const deliveryTimestamp = newDeliveryDate
        ? Timestamp.fromDate(new Date(newDeliveryDate))
        : undefined;

      await orderService.updateOrder(selectedOrder.id, {
        expectedDeliveryDate: deliveryTimestamp
      } as any);

      const updatedOrder = {
        ...selectedOrder,
        expectedDeliveryDate: deliveryTimestamp
      } as Sale;

      setSelectedOrder(updatedOrder);
      onOrderUpdate?.(updatedOrder);
      setShowDeliveryDateModal(false);
      setNewDeliveryDate('');
    } catch (error) {
      console.error('Error updating delivery date:', error);
      alert('Error updating delivery date. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openDeliveryDateModal = () => {
    // Pre-fill with current date if exists
    const currentDate = (selectedOrder as any)?.expectedDeliveryDate;
    if (currentDate) {
      const date = currentDate.toDate ? currentDate.toDate() : new Date(currentDate);
      setNewDeliveryDate(date.toISOString().split('T')[0]);
    } else {
      setNewDeliveryDate('');
    }
    setShowDeliveryDateModal(true);
  };

  // Use centralized helper for status colors
  const getStatusColor = getOrderStatusColor;

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'in_progress':
        return <Play className="h-4 w-4" />;
      case 'ready':
        return <Truck className="h-4 w-4" />;
      case 'delivered':
      case 'complete':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  // Use centralized helpers
  const formatDate = toDate;
  const formatCurrency = formatCurrencyHelper;

  const openReceipt = (type: 'order' | 'payment' | 'completion') => {
    setReceiptType(type);
    setShowReceipt(true);
  };

  if (!isOpen || !selectedOrder) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl sm:max-w-3xl md:max-w-4xl shadow-lg rounded-md bg-white mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Order Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Order Header */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Order Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Order ID:</span>
                        <span className="font-medium">#{selectedOrder.orderId || selectedOrder.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Order Date:</span>
                        <span className="font-medium">{format(formatDate(selectedOrder.orderDate), 'MMM dd, yyyy')}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Expected Delivery:</span>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${!(selectedOrder as any).expectedDeliveryDate ? 'text-gray-400 italic' : ''}`}>
                            {(selectedOrder as any).expectedDeliveryDate
                              ? format(formatDate((selectedOrder as any).expectedDeliveryDate), 'MMM dd, yyyy')
                              : 'Not Set'}
                          </span>
                          <button
                            onClick={openDeliveryDateModal}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Edit delivery date"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Trays:</span>
                        <span className="font-medium">{selectedOrder.totalTrays}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Financial Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="font-medium">{formatCurrency(selectedOrder.totalAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount Paid:</span>
                        <span className="font-medium text-green-600">{formatCurrency(selectedOrder.amountPaid)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Remaining:</span>
                        <span className="font-medium text-red-600">{formatCurrency(selectedOrder.remainingAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Progress:</span>
                        <span className="font-medium">
                          {Math.round((selectedOrder.amountPaid / selectedOrder.totalAmount) * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Status</h4>
                    <div className="space-y-3">
                      <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold ${getStatusColor(selectedOrder.status.name)}`}>
                        {getStatusIcon(selectedOrder.status.name)}
                        {selectedOrder.status.name.toUpperCase()}
                      </span>
                      <button
                        onClick={() => {
                          setNewStatus(selectedOrder.status.id);
                          setShowStatusModal(true);
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <Edit className="h-4 w-4" />
                        Change Status
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete Order
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plant Details Section */}
              {(() => {
                const orderItems = getOrderItems(selectedOrder);
                if (orderItems.length === 0) return null;

                return (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center mb-4">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                          <Leaf className="h-5 w-5 text-white" />
                        </div>
                        {orderItems.length > 1 ? `Order Items (${orderItems.length})` : 'Plant Details'}
                      </h4>
                    </div>

                    {orderItems.length === 1 ? (
                      // Single item display (legacy or single-plant order)
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-600 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/50 dark:to-emerald-900/50 rounded-xl flex items-center justify-center">
                              <Leaf className="h-7 w-7 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                              <h5 className="text-lg font-bold text-gray-900 dark:text-white">{orderItems[0].plantName}</h5>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {orderItems[0].plantBrand && (
                                  <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700">
                                    Brand: {orderItems[0].plantBrand}
                                  </span>
                                )}
                                {orderItems[0].plantVariety && (
                                  <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 rounded-full border border-purple-200 dark:border-purple-700">
                                    Variety: {orderItems[0].plantVariety}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="text-right">
                              <div className="text-sm text-gray-500 dark:text-gray-400">Quantity</div>
                              <div className="text-xl font-bold text-gray-900 dark:text-white">
                                {orderItems[0].quantity?.toLocaleString() || '-'} plants
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-500 dark:text-gray-400">Trays Required</div>
                              <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                                {orderItems[0].traysRequired} trays
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Multi-item table display
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Plant</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Brand/Variety</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Qty</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Rate</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Amount</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Trays</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {orderItems.map((item, index) => (
                              <tr key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.plantName}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                  {item.plantBrand}{item.plantVariety ? ` - ${item.plantVariety}` : ''}
                                </td>
                                <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.quantity.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-600 dark:text-gray-400">{formatCurrency(item.pricePerPlant)}</td>
                                <td className="px-4 py-3 text-sm text-right font-medium text-green-600 dark:text-green-400">{formatCurrency(item.totalPrice)}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-600 dark:text-gray-400">{item.traysRequired}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">Total</td>
                              <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                                {orderItems.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-sm text-right font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(orderItems.reduce((sum, item) => sum + item.totalPrice, 0))}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                                {orderItems.reduce((sum, item) => sum + item.traysRequired, 0)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Modern Customer Information */}
              {customer && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center mb-6">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      Customer Details
                    </h4>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Customer Profile & Contact Card */}
                    <div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-600 shadow-sm">
                        <div className="flex flex-col items-center text-center space-y-4 mb-4">
                          {/* Profile Avatar */}
                          <div className="relative">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-white text-lg font-bold">
                                {customer.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                              </span>
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white dark:border-gray-800"></div>
                          </div>
                          
                          {/* Customer Name */}
                          <div>
                            <h5 className="text-lg font-bold text-gray-900 dark:text-white">{customer.name}</h5>
                          </div>
                        </div>

                        {/* Contact Information */}
                        <div className="border-t border-gray-100 dark:border-gray-600 pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.phone}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Primary contact</p>
                              </div>
                            </div>
                            <a
                              href={`tel:${customer.phone.replace(/\\D/g, '')}`}
                              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 border border-transparent rounded-xl hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                            >
                              <Phone className="h-4 w-4" />
                              Call Now
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-600 shadow-sm h-full">
                        <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                          Address Details
                        </h6>
                        <div className="space-y-3">
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mt-0.5">
                              <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed">{customer.address}</p>
                              {customer.distanceKm && (
                                <div className="mt-2">
                                  <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full border border-blue-200 dark:border-blue-700">
                                    📍 {customer.distanceKm}km from polyhouse
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment History */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Payment History
                  </h4>
                  {selectedOrder.remainingAmount > 0 && (
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-colors shadow-sm"
                    >
                      <Plus className="h-4 w-4" />
                      Add Payment
                    </button>
                  )}
                </div>

                {payments.length > 0 ? (
                  <div className="space-y-3">
                    {payments.map((payment) => (
                      <div key={payment.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(payment.amount)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {format(formatDate(payment.paymentDate), 'MMM dd, yyyy')} • {payment.paymentMethod}
                            </div>
                          </div>
                        </div>
                        {payment.notes && (
                          <div className="text-xs text-gray-500 max-w-xs truncate">
                            {payment.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No payments recorded yet</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => openReceipt('order')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Receipt className="h-4 w-4" />
                  Generate Receipt
                </button>

                <button
                  onClick={onClose}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white mb-8">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Order Status</h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Order: <span className="font-medium">{selectedOrder.customerName}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Current Status: <span className="font-medium">{selectedOrder.status.name}</span>
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                >
                  {statusOptions.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.name} - {status.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowStatusModal(false);
                    setNewStatus('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusChange}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-2xl bg-white mb-8">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 -m-5 mb-4 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <IndianRupee className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Add Payment</h3>
                    <p className="text-green-100 text-sm">Record payment for this order</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentForm({ amount: '', paymentMethod: 'Cash', notes: '' });
                  }}
                  className="text-white/80 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleAddPayment} className="space-y-4">
              {/* Order Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Order ID:</span>
                    <span className="ml-2 font-semibold">#{selectedOrder.orderId || selectedOrder.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Customer:</span>
                    <span className="ml-2 font-semibold">{selectedOrder.customerName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Amount:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(selectedOrder.totalAmount)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Remaining:</span>
                    <span className="ml-2 font-semibold text-red-600">{formatCurrency(selectedOrder.remainingAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Amount *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedOrder.remainingAmount}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Maximum: {formatCurrency(selectedOrder.remainingAmount)}</p>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Card">Card</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentForm({ amount: '', paymentMethod: 'Cash', notes: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !paymentForm.amount}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Adding...' : 'Add Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-2xl bg-white">
            <div className="text-center">
              {/* Warning Icon */}
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="h-8 w-8 text-red-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Order?</h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this order? This action will mark the order as deleted and it will no longer appear in the order list.
              </p>

              {/* Order Details */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Order ID:</span>
                    <span className="ml-2 font-semibold">#{selectedOrder.orderId || selectedOrder.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Customer:</span>
                    <span className="ml-2 font-semibold">{selectedOrder.customerName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Amount:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(selectedOrder.totalAmount)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className="ml-2 font-semibold">{selectedOrder.status.name}</span>
                  </div>
                </div>
              </div>

              {/* Warning Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-left">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Payment records associated with this order will be preserved for accounting purposes.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrder}
                  disabled={deleting}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Date Edit Modal */}
      {showDeliveryDateModal && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-2xl bg-white">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 -m-5 mb-4 p-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Update Delivery Date</h3>
                    <p className="text-blue-100 text-sm">Set expected delivery date</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDeliveryDateModal(false);
                    setNewDeliveryDate('');
                  }}
                  className="text-white/80 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Order Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Order ID:</span>
                    <span className="ml-2 font-semibold">#{selectedOrder.orderId || selectedOrder.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Customer:</span>
                    <span className="ml-2 font-semibold">{selectedOrder.customerName}</span>
                  </div>
                </div>
              </div>

              {/* Date Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  value={newDeliveryDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setNewDeliveryDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to clear the delivery date</p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeliveryDateModal(false);
                    setNewDeliveryDate('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateDeliveryDate}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Date'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Generator */}
      {showReceipt && selectedOrder && customer && (
        <ReceiptGenerator
          order={selectedOrder}
          customer={customer}
          payments={payments}
          receiptType={receiptType}
          onClose={() => setShowReceipt(false)}
          onPrint={async () => {
            await printReceiptWithLogo(
              selectedOrder,
              customer,
              payments,
              receiptType
            );
          }}
        />
      )}
    </>
  );
};

export default OrderDetailsModal; 